/*â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 ðŸŒ Project: Ring2All Platform
 ðŸ§‘â€ðŸ’» Author: Rodrigo Cuadra
 ðŸ¢ Company: Ring2All LLC
 ðŸ“„ File: ParkingLotsFormView.tsx
 ðŸ—“ï¸ Created: 2025-01-27
 ðŸ§© Description:
    Form view for creating and editing Parking Lots.
    Manages parking lot configuration.

 ðŸ”§ Notes:
    - Domain-scoped data
    - Auto-generates context from name
    - Valet parking uses dynamic slots managed by FreeSWITCH

 ðŸ” Last Updated: 2025-11-07 by GPT-5 Codex (stability, i18n)
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ParkingCircle, Clock, Globe, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';

import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { deleteParkingLotById, navigateToParkingLotDuplicate } from './parkingLotsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useParkingLotData } from './hooks/useParkingLotData';
import { useParkingLotForm } from './hooks/useParkingLotForm';
import { createDefaultFormData } from './utils/formDefaults';
import type { ParkingLotFormState } from './types';
import { ParkingLotGeneralSection } from './components/ParkingLotGeneralSection';
import { ParkingLotCallBehaviorSection } from './components/ParkingLotCallBehaviorSection';

interface MOHSource {
  id: number;
  name: string;
  class_name: string;
  enabled: boolean;
}

export default function ParkingLotsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();
  const { selectedDomain } = useContextStore();

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<ParkingLotFormState>(createDefaultFormData());
  const [mohSources, setMohSources] = useState<MOHSource[]>([]);
  const [loadingMoh, setLoadingMoh] = useState(false);

  const {
    formData,
    setFormData,
    isLoading,
    loadParkingLot
  } = useParkingLotData({
    id,
    duplicateId,
    isEditMode
  });

  const {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    handleSave: handleSaveForm,
    handleCancel
  } = useParkingLotForm({
    formData,
    setFormData,
    originalFormData,
    isEditMode,
    parkingLotId: id,
    onLoadParkingLot: async (lotId: string) => {
      await loadParkingLot(lotId);
      setOriginalFormData(formData);
    }
  });

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSaveForm();

    // Update original data after successful save
    if (savedData) {
      setOriginalFormData({ ...formData });
    }

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSaveForm, formData, isEditMode, id]);

  // Use reusable hook for save actions
  const mode = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/parking',
    newPath: '/pbx/applications/parking/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/parking/${savedData.id || savedData.uuid}`,
    getFormData: () => formData,
    storageKey: 'parkingLots:saveAndNewData',
  });

  // Quick List (Parking Lots) state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [initialLots, setInitialLots] = useState<any[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [quickListLots, setQuickListLots] = useState<any[]>([]);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  // Update originalFormData when formData is loaded
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setOriginalFormData({ ...formData });
    }
  }, [formData]);

  // Load MOH sources
  useEffect(() => {
    const loadMOHSources = async () => {
      setLoadingMoh(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/moh-sources'), {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && Array.isArray(result.data)) {
            setMohSources(result.data.filter((moh: MOHSource) => moh.enabled));
          }
        }
      } catch (error) {
        console.error('Error loading MOH sources:', error);
      } finally {
        setLoadingMoh(false);
      }
    };

    loadMOHSources();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = useCallback((field: keyof ParkingLotFormState, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field as string];
        return updated;
      });
    }
  }, [errors, setFormData, setErrors]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteParkingLotById(id);

      showSuccess(t('parkingLots.messages.deleteSuccess'));
      navigate('/pbx/applications/parking');
    } catch (error) {
      showError(error?.message || t('parkingLots.messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, navigate, showSuccess, showError, t]);



  // Quick List loader for Parking Lots
  // Load initial lots when Quick List modal opens
  const loadInitialLots = useCallback(async () => {
    if (initialLots.length > 0) return; // Already loaded

    setIsLoadingInitial(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/parking-lots?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setInitialLots(result.data);
          setQuickListLots(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading initial lots:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialLots.length]);

  const loadQuickList = useCallback(async (search: string) => {
    const normalized = search.trim();

    // If search is empty or less than 2 characters, restore initial lots
    if (!normalized || normalized.length < 2) {
      setQuickListLots(initialLots);
      return;
    }

    setIsQuickSearching(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'name',
        sortOrder: 'asc',
        search: normalized
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/parking-lots?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setQuickListLots(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading quick list lots:', error);
    } finally {
      setIsQuickSearching(false);
    }
  }, [initialLots]);

  // Load initial lots when Quick List modal opens
  useEffect(() => {
    if (showQuickList && initialLots.length === 0) {
      loadInitialLots();
    }
  }, [showQuickList, initialLots.length, loadInitialLots]);

  // Debounced Quick List search
  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        // Restore initial lots if search is cleared
        setQuickListLots(initialLots);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialLots]);

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToParkingLotDuplicate(navigate, id);
  };

  // Navigate to new
  const handleAddNew = () => {
    navigate('/pbx/applications/parking/new');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
        icon={<ParkingCircle className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('parkingLots.editTitle')} - ${formData.name || ''}` : t('parkingLots.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate('/pbx/applications/parking')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('parkingLots.actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('parkingLots.actions.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('parkingLots.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-6">
          <ParkingLotGeneralSection
            formData={formData}
            errors={errors}
            onChange={handleChange}
            t={t}
          />

          <ParkingLotCallBehaviorSection
            formData={formData}
            mohSources={mohSources}
            loadingMoh={loadingMoh}
            onChange={handleChange}
            t={t}
          />

          <FormBox
            title={t('parkingLots.sections.timeout')}
            icon={<Clock className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              <Label htmlFor="timeoutModule">
                <SimpleTooltip content={t('parkingLots.fields.timeoutModule.tooltip')}>
                  {t('parkingLots.fields.timeoutModule.label')}
                </SimpleTooltip>
              </Label>
              <div className="col-span-3">
                <ModuleDestinationSelect
                  moduleValue={formData.timeoutModule || ''}
                  destinationValue={formData.timeoutDestination || ''}
                  onModuleChange={(value) => handleChange('timeoutModule', value || '')}
                  onDestinationChange={(value) => handleChange('timeoutDestination', value || '')}
                  modulePlaceholder={t('parkingLots.fields.timeoutModule.placeholder')}
                  destinationPlaceholder={t('parkingLots.fields.timeoutDestination.placeholder')}
                />
              </div>
            </div>
          </FormBox>
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="parkingLots:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('parkingLots.deleteTitle')}
        message={t('parkingLots.messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal for Parking Lots */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('parkingLots.title')}
        items={quickListLots}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(lot) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/parking/${lot.uuid || lot.id}`);
        }}
        currentItemId={id}
        renderItem={(lot, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/parking/${lot.uuid || lot.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <ParkingCircle className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">
                    {lot.extension}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('parkingLots.current', 'Current')}
                    </span>
                  )}
                </div>
                {lot.name && (
                  <div className="text-sm text-muted-foreground truncate">
                    {lot.name}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('parkingLots.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('parkingLots.quickListEmpty', 'Start typing to search parking lots')}
        noResultsMessage={t('parkingLots.quickListNoResults', 'No parking lots found')}
        headerIcon={<ParkingCircle className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}

